/**
 * UrlCaptureDialog — paste a landing-page URL or a direct video file URL.
 * Pages are captured server-side as a full-page screenshot. Direct video FILE
 * urls skip capture (the file IS the asset). YouTube / Vimeo / Drive / Dropbox
 * links are refused: twins need keyframes we cannot extract from those hosts.
 *
 * The video classification happens HERE, client-side, before `captureUrl` is
 * called: the capture endpoint never sets `source`, so a caller reading
 * `result.source` only ever sees what this dialog put there.
 */

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  isDirectVideoUrl,
  nonKeyframeableVideoMessage,
} from '@digital-twins/shared-types'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { UrlCaptureResult } from '@/lib/api'
import {
  friendlyCaptureError,
  type CaptureUrl,
} from '@/components/media/useUrlCaptures'

/**
 * Turn an accepted capture into a PNG File, so it flows through the normal image
 * upload path (preview, slicing, twin vision) exactly like a manual upload.
 */
export function capturedImageToFile(
  result: UrlCaptureResult,
  filename = 'landing-page.png',
): File | null {
  if (!result.screenshotBase64) return null
  try {
    const binary = atob(result.screenshotBase64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
    return new File([bytes], filename, { type: 'image/png' })
  } catch {
    return null
  }
}

interface UrlCaptureDialogProps {
  /** The capture on the caller's own HTTP client. */
  capture: CaptureUrl
  open: boolean
  onOpenChange: (open: boolean) => void
  onAccept: (result: UrlCaptureResult) => void
  /** Retina factor for the capture (chat passes 1 to keep the image small). */
  scale?: number
  title?: string
}

export function UrlCaptureDialog({
  capture,
  open,
  onOpenChange,
  onAccept,
  scale,
  title = 'Add asset',
}: UrlCaptureDialogProps) {
  const { t } = useTranslation('app')
  const [url, setUrl] = useState('')
  const [fetching, setFetching] = useState(false)
  const [result, setResult] = useState<UrlCaptureResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setUrl('')
    setFetching(false)
    setResult(null)
    setError(null)
  }
  const close = () => {
    onOpenChange(false)
    setTimeout(reset, 200)
  }

  const fetchCapture = async () => {
    const u = url.trim()
    if (!u) return
    setFetching(true)
    setError(null)
    setResult(null)
    try {
      // FIRST, before anything is fetched. Screenshotting one of these produces
      // a still of a player page that looks like a successful capture and is
      // useless as a stimulus.
      const blocked = nonKeyframeableVideoMessage(u)
      if (blocked) {
        setError(blocked)
        return
      }
      // A direct video file is the asset itself; there is no page to capture.
      if (isDirectVideoUrl(u)) {
        const normalized = /^https?:\/\//i.test(u) ? u : `https://${u}`
        setResult({
          ok: true,
          publicUrl: normalized,
          pageTitle: 'Video',
          source: 'video',
        })
        return
      }
      const res = await capture(u, { scale, fullPage: true })
      if (res.ok && (res.publicUrl || res.screenshotBase64)) {
        setResult(res)
      } else {
        setError(friendlyCaptureError(res.failureReason, res.detail))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Capture failed.')
    } finally {
      setFetching(false)
    }
  }

  const previewSrc = result
    ? result.publicUrl ||
      (result.screenshotBase64 ? `data:image/png;base64,${result.screenshotBase64}` : '')
    : ''
  const videoPreviewUrl =
    result?.source === 'video' ? result.publicUrl || '' : ''

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t('questionnaire.urlCapture.description')}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="flex flex-col gap-3" data-testid="url-capture-fetch">
            <div className="flex gap-2">
              <Input
                type="url"
                inputMode="url"
                placeholder={t('questionnaire.urlCapture.placeholder')}
                value={url}
                disabled={fetching}
                autoFocus
                data-testid="url-capture-input"
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void fetchCapture()
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => void fetchCapture()}
                disabled={fetching || !url.trim()}
                data-testid="url-capture-fetch-btn"
              >
                {fetching ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Capturing…
                  </>
                ) : (
                  'Fetch'
                )}
              </Button>
            </div>
            {fetching && (
              <p className="text-sm text-muted-foreground">
                Capturing the full page — this can take ~20-30s.
              </p>
            )}
            {error && (
              <p className="text-sm text-[#E5484D]" data-testid="url-capture-error">
                {error}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="url-capture-preview">
            {videoPreviewUrl ? (
              <div className="overflow-hidden rounded-md border border-border bg-background">
                <video
                  src={videoPreviewUrl}
                  controls
                  className="aspect-video w-full"
                  data-testid="url-capture-preview-video"
                />
              </div>
            ) : (
              <div className="max-h-[55vh] overflow-y-scroll rounded-md border border-border bg-background [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-foreground/50">
                <img
                  src={previewSrc}
                  alt={result.pageTitle || 'Captured page'}
                  className="w-full"
                  data-testid="url-capture-preview-img"
                />
              </div>
            )}
            <p className="truncate text-xs text-muted-foreground">
              {videoPreviewUrl
                ? `${result.pageTitle || 'Video'} · twins will watch the video`
                : `${result.pageTitle || url} · scroll to see the full page`}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setResult(null)
                  setError(null)
                }}
              >
                {videoPreviewUrl ? 'Change URL' : 'Recapture'}
              </Button>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="url-capture-accept"
                onClick={() => {
                  onAccept(result)
                  close()
                }}
              >
                {videoPreviewUrl ? 'Use this video' : 'Use this image'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
